<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { RouterLink } from 'vue-router'
import { ROUTES } from '../../../core/config/constants.js'
import { getFirebaseDb } from '../../../core/firebase/firebase.js'
import { doc, getDoc } from 'firebase/firestore'

// City coverage state owned by this section — no other section needs it.
const cities = ref([])
const loadingCities = ref(true)
const selectedCity = ref(null)
const checkingCoverage = ref(false)
const coverageResult = ref(null) // null = not checked, true = covered, false = not covered

const citySearchQuery = ref('')
const dropdownOpen = ref(false)
const highlightedIndex = ref(-1)
const searchInputRef = ref(null)

const filteredCities = computed(() => {
  if (!citySearchQuery.value.trim()) return cities.value
  const query = citySearchQuery.value.trim().toLowerCase()
  return cities.value.filter((city) => city.toLowerCase().includes(query))
})

const openDropdown = () => {
  if (loadingCities.value) return
  dropdownOpen.value = true
  highlightedIndex.value = -1
}

const closeDropdown = () => {
  dropdownOpen.value = false
  highlightedIndex.value = -1
  if (!selectedCity.value) {
    citySearchQuery.value = ''
  } else {
    citySearchQuery.value = selectedCity.value
  }
}

const selectCity = (city) => {
  selectedCity.value = city
  citySearchQuery.value = city
  closeDropdown()
}

const clearSearch = () => {
  citySearchQuery.value = ''
  selectedCity.value = null
  highlightedIndex.value = -1
  searchInputRef.value?.focus()
}

const handleSearchInput = () => {
  if (!dropdownOpen.value) {
    dropdownOpen.value = true
  }
  highlightedIndex.value = -1
  if (selectedCity.value && citySearchQuery.value !== selectedCity.value) {
    selectedCity.value = null
  }
}

const handleSearchKeydown = (e) => {
  if (!dropdownOpen.value && (e.key === 'ArrowDown' || e.key === 'Enter')) {
    openDropdown()
    return
  }

  if (e.key === 'ArrowDown') {
    e.preventDefault()
    if (highlightedIndex.value < filteredCities.value.length - 1) {
      highlightedIndex.value++
    }
  } else if (e.key === 'ArrowUp') {
    e.preventDefault()
    if (highlightedIndex.value > 0) {
      highlightedIndex.value--
    }
  } else if (e.key === 'Enter') {
    e.preventDefault()
    if (highlightedIndex.value >= 0 && filteredCities.value[highlightedIndex.value]) {
      selectCity(filteredCities.value[highlightedIndex.value])
    } else if (filteredCities.value.length === 1) {
      selectCity(filteredCities.value[0])
    }
  } else if (e.key === 'Escape') {
    closeDropdown()
    searchInputRef.value?.blur()
  }
}

const handleClickOutside = (e) => {
  // Walk the actual DOM upward; works even though the wrapper element lives
  // inside this scoped component because closest() is unscoped.
  const wrapper = e.target.closest('.checker-input-wrapper')
  if (!wrapper && dropdownOpen.value) {
    closeDropdown()
  }
}

const getCities = async () => {
  try {
    const db = getFirebaseDb()
    const citiesDoc = await getDoc(doc(db, 'Counters', 'cities'))
    if (citiesDoc.exists()) {
      cities.value = citiesDoc.data().cities || []
    }
  } catch (error) {
    console.error('Error fetching cities:', error)
    cities.value = []
  } finally {
    loadingCities.value = false
  }
}

const checkCoverage = async () => {
  if (!selectedCity.value) return

  checkingCoverage.value = true
  coverageResult.value = null

  // Simulate checking animation
  await new Promise((resolve) => setTimeout(resolve, 1500))

  coverageResult.value = cities.value.includes(selectedCity.value)
  checkingCoverage.value = false
}

const resetCoverageCheck = () => {
  selectedCity.value = null
  coverageResult.value = null
  citySearchQuery.value = ''
  dropdownOpen.value = false
}

onMounted(() => {
  getCities()
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<template>
  <section class="coverage-section">
    <div class="container">
      <div class="coverage-checker">
        <!-- Header -->
        <div class="checker-header">
          <span class="section-badge">בדיקת כיסוי</span>
          <h2 class="section-title">בדקו אם אנחנו מגיעים אליכם</h2>
          <p class="checker-subtitle">בחרו את העיר שלכם ובדקו אם אנחנו מספקים לאזור שלכם</p>
        </div>

        <!-- Checker Card -->
        <div class="checker-card">
          <!-- Initial State - Select City -->
          <div v-if="coverageResult === null && !checkingCoverage" class="checker-form">
            <div class="checker-input-wrapper">
              <svg
                class="checker-icon"
                viewBox="0 0 24 24"
                width="24"
                height="24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              <input
                ref="searchInputRef"
                v-model="citySearchQuery"
                type="text"
                class="checker-select"
                :class="{ 'has-clear': citySearchQuery }"
                :placeholder="loadingCities ? 'טוען ערים...' : 'הקלידו או בחרו עיר'"
                :disabled="loadingCities"
                @focus="openDropdown"
                @input="handleSearchInput"
                @keydown="handleSearchKeydown"
                autocomplete="off"
              />
              <svg
                v-if="citySearchQuery"
                class="clear-icon"
                viewBox="0 0 24 24"
                width="18"
                height="18"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                @click="clearSearch"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
              <svg
                class="select-arrow"
                :class="{ 'arrow-up': dropdownOpen, 'with-clear': citySearchQuery }"
                viewBox="0 0 24 24"
                width="20"
                height="20"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                @click="dropdownOpen ? closeDropdown() : openDropdown()"
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
              <!-- Dropdown -->
              <div v-if="dropdownOpen" class="city-dropdown">
                <div v-if="filteredCities.length === 0" class="dropdown-empty">לא נמצאו ערים</div>
                <div
                  v-for="(city, index) in filteredCities"
                  :key="city"
                  class="dropdown-item"
                  :class="{
                    highlighted: index === highlightedIndex,
                    selected: city === selectedCity,
                  }"
                  @click="selectCity(city)"
                  @mouseenter="highlightedIndex = index"
                >
                  {{ city }}
                </div>
              </div>
            </div>
            <button
              @click="checkCoverage"
              class="btn-check"
              :disabled="!selectedCity"
              :class="{ disabled: !selectedCity }"
            >
              <svg
                viewBox="0 0 24 24"
                width="20"
                height="20"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
              <span>בדוק כיסוי</span>
            </button>
          </div>

          <!-- Checking State - Animation -->
          <div v-else-if="checkingCoverage" class="checker-loading">
            <div class="loading-animation">
              <div class="loading-map">
                <svg
                  viewBox="0 0 24 24"
                  width="64"
                  height="64"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.5"
                >
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
              </div>
              <div class="loading-pulse"></div>
              <div class="loading-pulse loading-pulse-2"></div>
              <div class="loading-pulse loading-pulse-3"></div>
            </div>
            <p class="loading-text">בודק כיסוי ל{{ selectedCity }}...</p>
          </div>

          <!-- Result State - Covered -->
          <div v-else-if="coverageResult === true" class="checker-result result-success">
            <div class="result-icon success">
              <svg
                viewBox="0 0 24 24"
                width="48"
                height="48"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            </div>
            <h3 class="result-title">מעולה! אנחנו מגיעים ל{{ selectedCity }}</h3>
            <p class="result-desc">הצטרפו עכשיו והתחילו ליהנות ממשלוחים ישירים לעסק שלכם</p>
            <div class="result-actions">
              <RouterLink :to="{ name: ROUTES.JOIN_NETWORK }" class="btn-join">
                <span>הצטרפו עכשיו</span>
                <svg
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </RouterLink>
              <button @click="resetCoverageCheck" class="btn-reset">בדוק עיר אחרת</button>
            </div>
          </div>

          <!-- Result State - Not Covered -->
          <div v-else-if="coverageResult === false" class="checker-result result-not-covered">
            <div class="result-icon not-covered">
              <svg
                viewBox="0 0 24 24"
                width="48"
                height="48"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M12 8v4M12 16h.01" />
              </svg>
            </div>
            <h3 class="result-title">עדיין לא מגיעים ל{{ selectedCity }}</h3>
            <p class="result-desc">
              אבל אנחנו מתרחבים כל הזמן! השאירו פרטים ונעדכן אתכם כשנגיע לאזור שלכם
            </p>
            <div class="result-actions">
              <RouterLink :to="{ name: ROUTES.JOIN_NETWORK }" class="btn-notify">
                <span>עדכנו אותי</span>
              </RouterLink>
              <button @click="resetCoverageCheck" class="btn-reset">בדוק עיר אחרת</button>
            </div>
          </div>
        </div>

        <!-- Features -->
        <div class="checker-features">
          <div class="checker-feature">
            <svg
              viewBox="0 0 24 24"
              width="24"
              height="24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <rect x="1" y="3" width="15" height="13" />
              <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
              <circle cx="5.5" cy="18.5" r="2.5" />
              <circle cx="18.5" cy="18.5" r="2.5" />
            </svg>
            <span>משלוחים יומיים</span>
          </div>
          <div class="checker-feature">
            <svg
              viewBox="0 0 24 24"
              width="24"
              height="24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            <span>זמני אספקה קבועים</span>
          </div>
          <div class="checker-feature">
            <svg
              viewBox="0 0 24 24"
              width="24"
              height="24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
            <span>אמינות מלאה</span>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style src="./styles/section-header.css" scoped></style>
<style src="./styles/coverage-base.css" scoped></style>
<style src="./styles/coverage-result.css" scoped></style>

<style scoped>
.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--spacing-md);
}

@media (min-width: 768px) {
  .container {
    padding: 0 var(--spacing-lg);
  }
}
</style>
